import type { AppState } from '../state/store';
import type { EquippedCosmetic, HubEnterResponse, HubEquip, HubInspectResponse, HubPresenceEntry } from '../api/types';
import { btn, esc, pill, row, shortAddr } from './components';
import { iconRow } from './icons';
import { formatFerryPassCountdown } from './dashboard';
import {
  HUB_STATIONS,
  nearbyStationForPosition,
  localHubPosition,
  stationById,
  type HubStation,
} from './waystationPlaza';

import { HUB_EMOTES, hubEmoteById } from '../hub/emotes';

const MAX_SCENE_PRESENCE = 50;

interface HubStandeePlacement {
  left: number;
  top: number;
}

function hashText(value: string): number {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function hubStandeePlacement(accountId: string, index: number): HubStandeePlacement {
  const hash = hashText(accountId || `presence-${index}`);
  const slot = (hash + index * 7) % MAX_SCENE_PRESENCE;
  const col = slot % 10;
  const rowIndex = Math.floor(slot / 10);
  const jitterX = ((hash >>> 8) % 17) / 10;
  const jitterY = ((hash >>> 16) % 11) / 10;
  return {
    left: Number((8 + col * 8.6 + jitterX).toFixed(1)),
    top: Number((23 + rowIndex * 11 + jitterY).toFixed(1)),
  };
}

function emoteLabel(emote: string): string {
  return hubEmoteById(emote)?.label ?? emote;
}

function equipLine(equipped: HubEquip[]): string {
  if (equipped.length === 0) return '<span class="muted">no gear shown</span>';
  return equipped
    .map((e) => `<span class="rarity rarity--${esc(e.rarity)}" title="${esc(e.slot)}">${esc(e.title)}</span>`)
    .join(' · ');
}

function cosmeticLine(cosmetics: EquippedCosmetic[]): string {
  if (cosmetics.length === 0) return '<span class="muted">no cosmetics equipped</span>';
  return cosmetics
    .map((c) => `<span class="cosmetic-tag" title="${esc(c.slot)}">${esc(c.title)}</span>`)
    .join(' ');
}

function stationCard(title: string, body: string): string {
  return `<section class="station-card"><h3>${esc(title)}</h3>${body}</section>`;
}

function stationEnabled(station: HubStation, state: AppState): boolean {
  return !station.devOnly || state.devMode;
}

function rewardButton(state: AppState, compact = false): string {
  const rewardEligible = state.hub?.access === 'full' && (state.pass?.balance ?? 0) > 0;
  return btn(compact ? 'Reward' : 'Start Reward run', 'start-reward', {}, {
    variant: rewardEligible ? 'primary' : 'ghost',
    disabled: !rewardEligible,
    title: rewardEligible ? 'spend a Pass and descend' : 'needs active eligibility + a Ferry Pass',
  });
}

function stationActionButtons(station: HubStation, state: AppState, compact = false): string {
  if (station.id === 'run') {
    return `${btn(compact ? 'Echo' : 'Start Echo run', 'start-echo', {}, { variant: 'primary' })}${rewardButton(state, compact)}`;
  }
  return btn(compact ? 'Open' : station.openLabel, station.primaryAct, {}, { variant: 'primary' });
}

function stationFallbackPanel(state: AppState): string {
  const hasRun = !!state.runId;
  const cards = HUB_STATIONS
    .filter((station) => stationEnabled(station, state))
    .map((station) => {
      const runHint = station.id === 'run' && hasRun
        ? `<p class="hint">Active run in progress — ${btn('return to run', 'goto-run', {}, { variant: 'ghost' })}</p>`
        : '';
      return stationCard(
        station.title,
        `<p class="muted">${esc(station.note)}</p><div class="btn-row">${stationActionButtons(station, state)}</div>${runHint}`,
      );
    })
    .join('');
  const ferry = stationCard(
    'Ferry Pass Status',
    `<p class="muted">Pass regeneration requires active $OBOL eligibility.</p>
     <div class="btn-row">${btn('Pass status & eligibility', 'goto-dashboard', {}, { variant: 'ghost' })}</div>
     ${state.pass ? `<p class="hint">Passes: <strong>${esc(state.pass.balance)}</strong>/${esc(state.pass.cap)} · <span data-ferry-pass-countdown data-next-regen-at="${esc(state.pass.next_regen_at ?? '')}" data-pass-balance="${esc(state.pass.balance)}" data-pass-cap="${esc(state.pass.cap)}" data-pass-frozen="${esc(String(state.pass.frozen))}" data-pass-eligibility="${esc(state.pass.eligibility_state)}"${state.pass.next_regen_at ? ` title="${esc(state.pass.next_regen_at)}"` : ''}>${esc(formatFerryPassCountdown(state.pass))}</span></p>` : ''}`,
  );
  return `
    <details class="waystation-fallback-cards" hidden data-mobile-fallback>
      <summary>Station list fallback</summary>
      <div class="stations">${cards}${ferry}</div>
    </details>`;
}

function emotePanel(state: AppState): string {
  const full = state.hub?.access === 'full';
  const mine = state.hub?.self.accountId;
  const me = state.hub?.presence.find((p) => p.accountId === mine);
  const current = me?.emote ?? null;
  // The standard set is free for everyone; any legacy cosmetic-gated emote the
  // player has equipped is appended after it.
  const extras = (me?.cosmetics ?? [])
    .filter((c) => c.kind === 'hub_emote')
    .map((c) => {
      const meta = c.metadata as { emoteId?: unknown };
      return typeof meta?.emoteId === 'string' ? meta.emoteId : null;
    })
    .filter((v): v is string => !!v && !HUB_EMOTES.some((e) => e.id === v));
  const buttons = [
    ...HUB_EMOTES.map((e) =>
      btn(e.label, 'hub-emote', { emote: e.id }, {
        variant: current === e.id ? 'primary' : 'ghost',
        disabled: !full,
      })),
    ...[...new Set(extras)].map((e) =>
      btn(emoteLabel(e), 'hub-emote', { emote: e }, {
        variant: current === e ? 'primary' : 'ghost',
        disabled: !full,
      })),
  ].join(' ');
  return `
    <section class="card waystation-support-card waystation-emotes-panel">
      <div class="run__head">
        <h2>Emotes</h2>
      </div>
      <div class="btn-row btn-row--wrap">${buttons}</div>
      ${full ? '' : '<p class="hint">Emotes need full (holder) access.</p>'}
    </section>`;
}

function presenceCard(entry: HubPresenceEntry, selfId: string | undefined): string {
  const isSelf = entry.accountId === selfId;
  const seconds = Math.max(0, Math.round(entry.ageMs / 1000));
  const emote = entry.emote ? `<span class="emote-tag">${esc(emoteLabel(entry.emote))}</span>` : '';
  return `
    <div class="presence">
      <div class="presence__head">
        <code>${esc(entry.walletLabel)}</code>
        ${isSelf ? pill('you', 'muted') : ''}
        ${emote}
      </div>
      <div class="presence__gear">${equipLine(entry.equipped)}</div>
      <div class="presence__gear">${cosmeticLine(entry.cosmetics)}</div>
      <div class="presence__foot">
        <span class="muted">seen ${seconds}s ago</span>
        <button
          type="button"
          class="btn btn--ghost"
          data-ui-control="button"
          data-act="hub-inspect"
          data-account="${esc(entry.accountId)}"
          aria-label="Inspect ${esc(entry.walletLabel)}"
        >Inspect</button>
      </div>
    </div>`;
}

function stationPoiMarkup(station: HubStation, state: AppState, nearbyId: string | null): string {
  if (!stationEnabled(station, state)) return '';
  const active = station.id === nearbyId;
  const disabled = active ? '' : 'disabled aria-disabled="true"';
  const percentX = ((station.footprint.x + station.footprint.width / 2) / 38) * 100;
  const percentY = ((station.footprint.y + station.footprint.height / 2) / 24) * 100;
  return `
    <button
      type="button"
      class="waystation-poi${active ? ' waystation-poi--nearby' : ''}"
      data-ui-control="button"
      data-station-poi="${esc(station.id)}"
      data-act="${esc(station.primaryAct)}"
      aria-label="${esc(station.title)}"
      style="--poi-x:${percentX.toFixed(2)}%;--poi-y:${percentY.toFixed(2)}%;"
      ${disabled}
    >
      <span class="sr-only">${esc(station.title)}</span>
    </button>`;
}

function sceneStatus(state: AppState, hub: HubEnterResponse): string {
  const full = hub.access === 'full';
  const reason = !full && hub.reason ? `<p class="hint">${esc(hub.reason)} ${btn('Re-check eligibility', 'refresh-eligibility', {}, { variant: 'ghost' })}</p>` : '';
  const accountId = state.account?.account.id ?? hub.self.accountId;
  return `
    <section class="card waystation-support-card waystation-status-panel" aria-label="Access and Ferry Pass summary">
      <div class="run__head">
        <h2>Status</h2>
        ${full ? pill('Full access', 'ok') : pill('Limited access', 'warn')}
      </div>
      ${row('Player', `<code>${esc(hub.self.walletLabel)}</code>`)}
      ${row('Account', `<code>${esc(shortAddr(accountId))}</code>`)}
      ${row('Eligibility', pill(hub.self.eligibilityState, full ? 'ok' : 'warn'))}
      ${state.pass ? iconRow('Ferry Passes', 'ferryPass', `<strong>${esc(state.pass.balance)}</strong> / ${esc(state.pass.cap)}`) : ''}
      ${reason}
    </section>`;
}

function supportPanels(state: AppState, hub: HubEnterResponse): string {
  return `
    <div class="waystation-support">
      ${sceneStatus(state, hub)}
      ${emotePanel(state)}
    </div>`;
}

function contextInteraction(state: AppState): string {
  if (!state.hub) return '';
  const local = localHubPosition(state);
  const station = stationById(state.hubNearbyStationId) ?? nearbyStationForPosition(local.position, state.devMode);
  if (!station || !stationEnabled(station, state)) return '';
  const secondary = station.id === 'run' ? rewardButton(state, true) : '';
  return `
    <div class="waystation-interaction" aria-live="polite">
      <strong>E — ${esc(station.openLabel)}</strong>
      <span>${esc(station.note)}</span>
      <div class="btn-row">
        ${station.id === 'run' ? btn('Echo', 'start-echo', {}, { variant: 'primary' }) : btn('Open', station.primaryAct, {}, { variant: 'primary' })}
        ${secondary}
      </div>
    </div>`;
}

function visualHubScene(state: AppState): string {
  const hub = state.hub!;
  const full = hub.access === 'full';
  const visiblePresence = full ? hub.presence.slice(0, MAX_SCENE_PRESENCE) : [];
  const nearby = state.hubNearbyStationId ?? nearbyStationForPosition(localHubPosition(state).position, state.devMode)?.id ?? null;
  const limitedNote = full ? '' : '<div class="waystation-plaza__limited">Presence is holders-only</div>';
  return `
    <section
      class="card card--wide waystation-plaza${full ? '' : ' waystation-plaza--limited'}"
      aria-label="Walkable Waystation plaza"
      data-presence-count="${esc(visiblePresence.length)}"
    >
      <canvas class="waystation-plaza__canvas" data-hub-plaza-canvas data-player-sprite="/assets/sprites/player/player.png" width="960" height="600" aria-label="Walkable Waystation plaza"></canvas>
      <div class="waystation-plaza__fallback" aria-hidden="true"></div>
      <div class="waystation-plaza__pois" aria-label="Station points of interest">
        ${HUB_STATIONS.map((station) => stationPoiMarkup(station, state, nearby)).join('')}
      </div>
      ${contextInteraction(state)}
      ${limitedNote}
    </section>`;
}

function presencePanel(state: AppState): string {
  const hub = state.hub!;
  const roster = hub.presence;
  const list = roster.length > 0
    ? `<div class="presence-list">${roster.map((p) => presenceCard(p, hub.self.accountId)).join('')}</div>`
    : `<p class="muted">${hub.access === 'full' ? 'No one else is here right now.' : 'Presence is holders-only — activate eligibility to join the hall.'}</p>`;
  return `
    <section class="card card--wide waystation-presence-panel" data-hub-player-list>
      <div class="run__head">
        <h2>Present in the plaza</h2>
        ${btn('Refresh', 'hub-refresh', {}, { variant: 'ghost' })}
      </div>
      ${list}
    </section>`;
}

function inspectPanel(inspect: HubInspectResponse): string {
  return `
    <section class="card card--wide inspect-panel">
      <div class="run__head">
        <h2>Inspecting <code>${esc(inspect.walletLabel)}</code></h2>
        ${btn('Close', 'hub-clear-inspect', {}, { variant: 'ghost' })}
      </div>
      ${row('Status', pill(inspect.status, inspect.status === 'active' ? 'ok' : 'warn'))}
      ${row('Equipped', equipLine(inspect.equipped))}
      ${row('Cosmetics', cosmeticLine(inspect.cosmetics))}
      <p class="hint">Read-only — inspecting never reveals private wallet, session, or balance data.</p>
    </section>`;
}

export function renderWaystation(state: AppState): string {
  if (!state.session) {
    return `<div class="grid"><section class="card"><h2>The Waystation</h2><p class="muted">Connect a wallet to enter the holders' plaza.</p></section></div>`;
  }
  if (!state.hub) {
    return `
      <div class="grid">
        <section class="card card--wide">
          <h2>The Waystation</h2>
          <p class="muted">Step into the plaza to see who else is between descents.</p>
          <div class="btn-row">${btn('Enter the Waystation', 'goto-waystation', {}, { variant: 'primary' })}</div>
        </section>
      </div>`;
  }
  return `
    <div class="grid waystation-hub">
      ${visualHubScene(state)}
      ${supportPanels(state, state.hub)}
      ${stationFallbackPanel(state)}
      ${state.hubInspect ? inspectPanel(state.hubInspect) : ''}
      ${presencePanel(state)}
    </div>`;
}
